import { useEffect, useState } from 'react';
import axios from 'axios';
import { formatDuration, formatAgo } from './syncTime';
import '../../styles/components/SyncProgress.scss';

const STATUS_LABELS = {
  running: 'Sincronizando',
  finished: 'Completada',
  failed: 'Falló',
  interrupted: 'Interrumpida',
};

const API_URL = process.env.REACT_APP_API_URL;

const COUNTERS = [
  { key: 'created', label: 'Nuevos' },
  { key: 'updated', label: 'Actualizados' },
  { key: 'skipped', label: 'Omitidos' },
  { key: 'errors',  label: 'Errores' },
];

/**
 * Progreso en vivo de una sincronización: barra (o contador si la bodega no
 * informa el total), contadores, tiempo transcurrido y qué está haciendo ahora.
 */
const SyncProgress = ({ run, lastLog }) => {
  const isRunning = run.status === 'running';

  // Reloj de 1 s para que el tiempo transcurrido avance entre sondeos.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isRunning) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isRunning]);

  const started = new Date(run.startedAt).getTime();
  const ended = run.finishedAt ? new Date(run.finishedAt).getTime() : now;
  const hasTotal = run.total > 0;
  const pct = hasTotal ? Math.min(100, Math.round((run.current / run.total) * 100)) : null;
  const counts = run.counts || run.summary || {};

  // Lista de productos nuevos de esta ejecución, cargada al pulsar "Nuevos".
  const [showCreated, setShowCreated] = useState(false);
  const [created, setCreated] = useState(null);
  const [createdError, setCreatedError] = useState('');

  useEffect(() => { setShowCreated(false); setCreated(null); }, [run.id]);

  const toggleCreated = async () => {
    const next = !showCreated;
    setShowCreated(next);
    if (!next) return;
    setCreatedError('');
    try {
      const { data } = await axios.get(`${API_URL}/admin/sync/runs/${run.id}/created`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admToken')}` },
      });
      setCreated(data.products);
    } catch (err) {
      setCreatedError(err.response?.data?.message || 'No se pudieron leer los productos nuevos.');
    }
  };

  // Antes del primer producto, la bodega está descargando su catálogo.
  const phase = isRunning && !run.current
    ? 'Descargando catálogo de la bodega…'
    : lastLog?.message;

  return (
    <div className={`sync-progress sync-progress--${run.status}`}>
      <div className="sync-progress__head">
        <h3>
          {STATUS_LABELS[run.status] || run.status} · {run.provider.toUpperCase()}
        </h3>
        <span className="sync-progress__time">
          {formatDuration(ended - started)}
          {isRunning && run.heartbeatAt && ` · actividad ${formatAgo(run.heartbeatAt, now)}`}
        </span>
      </div>

      <div className={`sync-progress__bar${hasTotal ? '' : ' sync-progress__bar--indeterminate'}`}>
        <div className="sync-progress__fill" style={hasTotal ? { width: `${pct}%` } : undefined} />
      </div>

      <p className="sync-progress__count">
        {hasTotal
          ? <><strong>{run.current}</strong> de <strong>{run.total}</strong> productos · {pct}%</>
          : <><strong>{run.current || 0}</strong> productos procesados</>}
      </p>

      <div className="sync-progress__counters">
        {COUNTERS.map(({ key, label }) => (
          key === 'created' && counts.created > 0 ? (
            <button
              key={key}
              type="button"
              className={`sync-progress__counter sync-progress__counter--${key} sync-progress__counter--clickable${showCreated ? ' is-open' : ''}`}
              onClick={toggleCreated}
              aria-expanded={showCreated}
            >
              <strong>{counts[key]}</strong>
              <span>{label} · {showCreated ? 'ocultar' : 'ver cuáles'}</span>
            </button>
          ) : (
            <div key={key} className={`sync-progress__counter sync-progress__counter--${key}`}>
              <strong>{counts[key] || 0}</strong>
              <span>{label}</span>
            </div>
          )
        ))}
      </div>

      {showCreated && (
        <div className="sync-progress__created">
          {createdError && <p>{createdError}</p>}
          {!createdError && !created && <p>Cargando…</p>}
          {created && created.length === 0 && <p>No se encontraron productos nuevos.</p>}
          {created && created.length > 0 && (
            <ul>
              {created.map((p) => (
                <li key={p.id}>
                  <code>{p.reference}</code>
                  {p.slug
                    ? <a href={`/productos/${p.slug}`} target="_blank" rel="noopener noreferrer">{p.title}</a>
                    : <span>{p.title}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {phase && isRunning && <p className="sync-progress__phase">{phase}</p>}

      {run.summary?.deactivation?.count > 0 && (
        <p className="sync-progress__hidden">
          Se ocultaron de la web <strong>{run.summary.deactivation.count}</strong> productos que la bodega ya no ofrece
          {run.summary.deactivation.references?.length > 0 && <>: <code>{run.summary.deactivation.references.join(', ')}</code></>}.
        </p>
      )}
      {run.summary?.deactivation?.skipped === 'too_many' && (
        <p className="sync-progress__note">
          No se ocultó ningún producto: faltaban {run.summary.deactivation.missing} de {run.summary.deactivation.active} y
          parece una respuesta incompleta de la bodega.
        </p>
      )}

      {run.status === 'interrupted' && (
        <p className="sync-progress__note">
          El servidor se reinició durante la sincronización (deploy o reinicio diario). Vuelve a lanzarla.
        </p>
      )}
      {run.status === 'failed' && (
        <p className="sync-progress__note">La sincronización falló. Revisa el log para ver el detalle.</p>
      )}
    </div>
  );
};

export default SyncProgress;
